import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { isTextColorRole } from '../tokens';
import type { TextColorRole } from '../tokens';

type ColorRoleFieldProps = {
  field: { label?: string };
  name: string;
  /**
   * Undefined when the prop is absent from saved page data — a component may make its color
   * optional so it can fall back to another color for pages authored before the prop existed
   * (see faq.tsx's headingColor). Treated as "no role selected", i.e. custom.
   */
  value: string | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

const ROLE_OPTIONS: readonly { label: string; value: TextColorRole }[] = [
  { label: 'Text', value: 'text' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Muted', value: 'muted' },
  { label: 'Inverted', value: 'inverted' },
  { label: 'Link', value: 'link' },
];

const CUSTOM_OPTION_VALUE = 'custom';

/** Starting color when switching from a theme role to a custom value. */
const CUSTOM_DEFAULT = '#121516';

const COMPLETE_HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Constrains free-typed input to hex characters.
 *
 * Whatever is typed here is persisted verbatim as the color, so without this a paste
 * of arbitrary text would be stored and then emitted as a CSS value. Partial input is
 * still allowed so the field can be typed into a digit at a time.
 *
 * Shared with background-field.tsx so both role pickers apply the same sanitisation.
 */
export function sanitizeHexInput(raw: string): string {
  return `#${raw.replace(/[^0-9a-fA-F]/g, '').slice(0, 6)}`;
}

const dropdownStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--dcm-cms-color-role-select-padding, 6px 8px)',
  border: `1px solid var(--dcm-cms-color-role-border-color, #d0d4d8)`,
  borderRadius: 'var(--dcm-cms-color-role-border-radius, 4px)',
  fontSize: 'var(--dcm-cms-color-role-font-size, 14px)',
  color: 'var(--dcm-cms-color-role-text-color, #485055)',
  background: 'var(--dcm-cms-color-role-bg, #fff)',
  cursor: 'pointer',
  boxSizing: 'border-box' as const,
};

/**
 * Puck custom field for text color, offering the theme's semantic color roles plus
 * a custom hex escape hatch via a dropdown. Roles are stored by key so the rendered
 * color keeps tracking merchant branding; a custom value is stored as the literal hex.
 */
export function ColorRoleField({ field, name, value = '', onChange, readOnly }: ColorRoleFieldProps): JSX.Element {
  const isCustom = !isTextColorRole(value);

  const selectValue = isCustom ? CUSTOM_OPTION_VALUE : value;

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const chosen = e.currentTarget.value;
      if (chosen === CUSTOM_OPTION_VALUE) {
        // Only seed the starting hex when not already in custom mode.
        if (!isCustom) onChange(CUSTOM_DEFAULT);
      } else {
        onChange(chosen);
      }
    },
    [isCustom, onChange]
  );

  const handleHexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(sanitizeHexInput(e.currentTarget.value)),
    [onChange]
  );

  const labelText = field.label ?? name;

  return (
    <FieldLabel label={labelText} readOnly={readOnly}>
      <select
        value={selectValue}
        onChange={handleSelectChange}
        disabled={readOnly}
        aria-label={labelText}
        data-testid={`color-role-select-${name}`}
        style={dropdownStyle}
      >
        {ROLE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        <option value={CUSTOM_OPTION_VALUE}>Custom</option>
      </select>

      {isCustom && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <input
            type='color'
            value={COMPLETE_HEX.test(value) ? value : CUSTOM_DEFAULT}
            onChange={handleHexChange}
            disabled={readOnly}
            aria-label={`${labelText} custom color swatch`}
            data-testid={`color-role-swatch-${name}`}
            style={{
              width: 32,
              height: 32,
              padding: 0,
              border: `1px solid var(--dcm-cms-color-role-border-color, #d0d4d8)`,
              borderRadius: 'var(--dcm-cms-color-role-border-radius, 4px)',
              cursor: 'pointer',
            }}
          />
          <input
            type='text'
            value={value}
            onChange={handleHexChange}
            disabled={readOnly}
            aria-label={`${labelText} custom color value`}
            data-testid={`color-role-hex-${name}`}
            style={{
              flex: 1,
              padding: 'var(--dcm-cms-color-role-select-padding, 6px 8px)',
              border: `1px solid var(--dcm-cms-color-role-border-color, #d0d4d8)`,
              borderRadius: 'var(--dcm-cms-color-role-border-radius, 4px)',
              fontSize: 'var(--dcm-cms-color-role-font-size, 14px)',
              fontFamily: 'var(--dcm-cms-color-role-font-family, monospace)',
            }}
          />
        </div>
      )}
    </FieldLabel>
  );
}
